import React, {useState, useEffect} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CarIcon from '@mui/icons-material/DirectionsCar';
import { api } from 'common';
import { colors } from '../components/Theme/WebTheme';
import { useTranslation } from "react-i18next";
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MoneyIcon from '@mui/icons-material/Money';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { Category } from '@mui/icons-material';
import QueueIcon from "@mui/icons-material/Queue";

const drawerWidth = 260;

export default function ResponsiveDrawer(props) {
  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const {
    signOff
  } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();

  const LogOut = () => {
    dispatch(signOff());
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if(auth.profile && auth.profile.usertype){
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  // Check if admin queue addon is enabled
  const isAdminQueueAddonEnabled = settings?.admin_queue_addon_enabled === true;

  const hasMenuAccess = (menuItem) => {
    // Special case: essential menu items should always be available for all users
    if (menuItem.permission === "complain" && settings.support_module_enabled === true) {
      console.log("🔍 Complain available for admin:", menuItem.name);
      return false;
    }

    if (menuItem.permission === "support_management" && role === "admin" && settings.support_module_enabled === true) {
      console.log("🔍 Support management available for admin:", menuItem.name);
      return true;
    }

    if (menuItem.permission === "support_management" && role === "admin" && settings.support_module_enabled === false) {
      console.log("🔍 Support management available for admin:", menuItem.name);
      return false;
    }

    // Special case: bank details should be available for admin
    if (menuItem.permission === "admin_queue" && (role === "admin" || role === "fleetadmin")) {
      return true;
    }

    // Special case: bank details should be available for admin
    if (menuItem.permission === "admin_queue" && (role === "admin" || role === "fleetadmin")) {
      return true;
    }

    // Only if no custom role, then check admin usertype
    if (role === "admin") {
      return true;
    }

    return menuItem.access.includes(role);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div style={{ backgroundColor: colors.MAIN_COLOR, height: "100%" }}>
      <div
        style={{
          display: "flex",
          backgroundColor: colors.MAIN_COLOR,
          justifyContent: "center",
        }}
      >
        <img
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            width: "120px",
          }}
          src={require("../assets/img/logo138x75black.png").default}
          alt="Logo"
        />
      </div>
      <div style={{ backgroundColor: colors.MAIN_COLOR }}>
        {role ? (
          <List disablePadding={true} dense={false}>
            {[
              {
                name: t("home"),
                url: "/",
                icon: <HomeIcon />,
                access: ["admin", "fleetadmin", "driver", "customer"],
              },
              {
                name: t("dashboard_text"),
                url: "/dashboard",
                icon: <DashboardIcon />,
                access: ["admin", "fleetadmin"],
              },
              {
                name: t("booking_history"),
                url: "/bookings",
                icon: <FormatListBulletedIcon />,
                access: ["admin", "fleetadmin", "driver", "customer"],
              },
              // Admin Queue menu - only show when admin queue addon is enabled
              ...(isAdminQueueAddonEnabled
                ? [
                    {
                      name: t("admin_queue"),
                      url: "/queued-orders",
                      icon: <QueueIcon />,
                      access: ["admin", "fleetadmin"],
                      permission: "admin_queue",
                    },
                  ]
                : []),
              {
                name: t("addbookinglable"),
                url: "/addbookings",
                icon: <AddLocationAltOutlinedIcon />,
                access: ["admin", "fleetadmin", "customer"],
              },

              {
                name: t("user"),
                url: "/users",
                icon: <EmojiPeopleIcon />,
                access: ["admin", "fleetadmin"],
              },
              {
                name: t("pricing"),
                url: "/pricing",
                icon: <Category />,
                access: ["admin"],
              },
              {
                name: t("cars"),
                url: "/cars",
                icon: <CarIcon />,
                access: ["admin", "fleetadmin", "driver"],
              },
              {
                name: t("add_to_wallet"),
                url: "/addtowallet",
                icon: <AccountBalanceWalletOutlinedIcon />,
                access: ["admin"],
              },
              {
                name: t("withdraws"),
                url: "/withdraws",
                icon: <MoneyIcon />,
                access: ["admin"],
              },
              {
                name: t("promo"),
                url: "/promos",
                icon: <DiscountOutlinedIcon />,
                access: ["admin"],
              },
              {
                name: t("push_notification_title"),
                url: "/notifications",
                icon: <NotificationsActiveOutlinedIcon />,
                access: ["admin"],
              },
              {
                name: t("cancellation_reasons"),
                url: "/cancelreasons",
                icon: <GppBadOutlinedIcon />,
                access: ["admin"],
              },
              {
                name: t("report"),
                url: "/allreports",
                icon: <AssessmentOutlinedIcon />,
                access: ["admin", "fleetadmin"],
              },
              {
                name: t("complain"),
                url: "/complain",
                icon: <AnnouncementOutlinedIcon />,
                access: ["admin"],
                permission: "complain"
              },
              {
                name: t("complain"),
                url: "/support",
                icon: <AnnouncementOutlinedIcon />,
                access: ["admin"],
                permission: "support_management",
              },
              {
                name: t("settings_title"),
                url: "/settings",
                icon: <SettingsOutlinedIcon />,
                access: ["admin"],
              },
              {
                name: t("my_wallet_tile"),
                url: "/userwallet",
                icon: <AccountBalanceWalletOutlinedIcon />,
                access: ["driver", "customer", "fleetadmin"],
              },
              {
                name: t("profile"),
                url: "/profile",
                icon: <PermIdentityOutlinedIcon />,
                access: ["admin", "fleetadmin", "driver", "customer"],
              },
              {
                name: t("logout"),
                url: "logout",
                icon: <LogoutOutlinedIcon />,
                access: ["admin", "fleetadmin", "driver", "customer"],
              },
            ].map((item, index) =>
              item.access.includes(role) && hasMenuAccess(item) ? (
                <div
                  style={{ display: "flex", height: "50px" }}
                  key={"key" + index}
                >
                  <ListItem
                    key={item}
                    disableGutters={true}
                    disablePadding={true}
                    style={{ paddingLeft: 5 }}
                    alignItems="center"
                    dense={false}
                  >
                    <ListItemButton
                      disableGutters={true}
                      dense={false}
                      component={Link}
                      to={item.url === "logout" ? null : item.url}
                      onClick={item.url === "logout" ? LogOut : null}
                    >
                      <ListItemIcon style={{ color: colors.WHITE }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        inset={false}
                        disableTypography={false}
                        primary={item.name}
                        style={{
                          color: colors.WHITE,
                          textAlign: isRTL === "rtl" ? "right" : "left",
                        }}
                      />
                    </ListItemButton>
                    <Divider />
                  </ListItem>
                </div>
              ) : null
            )}
          </List>
        ) : null}
      </div>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex',direction:isRTL === 'rtl'? 'rtl':'ltr'}}>
      <CssBaseline />
      <AppBar
        position="fixed"
        style={isRTL=== 'rtl'? {marginRight:drawerWidth,backgroundColor: colors.MAIN_COLOR,}:{marginLeft:drawerWidth,backgroundColor: colors.MAIN_COLOR,}}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color={colors.WHITE}
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2,  display: { sm: 'none' }, }}
          >
            <MenuIcon style={{color:colors.WHITE}} />
          </IconButton>
          <Typography variant="h6" noWrap component="div" style={{color:colors.WHITE}}>
          {settings && settings.appName? settings.appName: '' }
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },direction:isRTL === 'rtl'? 'rtl':'ltr'
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },direction:isRTL === 'rtl'? 'rtl':'ltr'
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar/>
        {props.children}
      </Box>
    </Box>
  );
}